import { useState, useEffect, useContext } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  MenuItem,
  Typography,
  Divider,
  Autocomplete,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarTiposPPE = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const [formData, setFormData] = useState({
    tipcodigo: "",
    tipdescri: "",
    tipaniodep: 0,
    tipmesdep: 0,
    tipporresidual: 0,
    tipctadepre: "",
    tipctagasdep: "",
    tipctacon: "",
    tipstatus: "A",
  })

  // --- TU CÓDIGO GANADOR IMPLEMENTADO ---
  const { data: rawCuentas, isLoading: isLoadingCuentas } = useQuery({
    queryKey: ["cuentasContablesPPE"],
    queryFn: async () => {
      try {
        const response = await api.get("/TiposPPE/getCuentasContables")
        const dataExtraida = response?.data?.data || response?.data || response || []
        return dataExtraida
      } catch (error) {
        console.error("Error al cargar cuentas contables:", error)
        return []
      }
    },
  })

  // Protección estricta: garantizamos que sea un Array
  const cuentasValidadas = Array.isArray(rawCuentas)
    ? rawCuentas
    : Array.isArray(rawCuentas?.data)
      ? rawCuentas.data
      : []

  // Mapeamos al formato exacto {id, label} que exige el Autocomplete de Material UI
  const listaCuentas = cuentasValidadas.map((item) => ({
    id: item.ctacodigo || item.id,
    label: item.label || `${item.ctacodigo} - ${item.ctadescri || ""}`,
  }))
  // --- FIN DE TU CÓDIGO ---

  useEffect(() => {
    if (location.state) {
      setFormData((prev) => ({ ...prev, ...location.state }))
    } else {
      navigate("/home/dashboard/TiposPPE")
    }
  }, [location, navigate])

  const { mutateAsync: SaveEdicionTipo, isPending: isSavingEdicion } = useMutation({
    queryKey: ["isUpdatingTipo"],
    fn: async (data) => {
      const response = await api.post("/TiposPPE/updateTiposPPE", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  const handleInputChange = (field, value) => {
    let finalValue = value
    if (field === "tipdescri") finalValue = value.toUpperCase().slice(0, 100)
    else if (["tipaniodep", "tipmesdep", "tipporresidual"].includes(field)) finalValue = Number(value)

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    if (!formData.tipdescri.trim()) return showWarning("La descripción es obligatoria")
    await SaveEdicionTipo({ ...formData })
  }

  const actualizarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (a) => a?.acccaption === "ACTUALIZAR" || a?.acccaption === "GUARDAR" || a?.acccaption === "EDITAR",
  )
  const toolbarActions = actualizarAction
    ? [
        {
          label: actualizarAction.acccaption,
          key: actualizarAction.acccaption,
          icon: getIconComponent(actualizarAction.accnameicono, actualizarAction.acctipoico),
        },
      ]
    : [{ label: "Actualizar", key: "ACTUALIZAR", icon: <Save /> }]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <Box>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingEdicion || isLoadingCuentas}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Editar Tipo de Activo: {formData.tipcodigo}</b>
        </div>

        <CustomBackdrop isLoading={isSavingEdicion || isLoadingCuentas} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 3, mb: 3, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos Generales
            </Typography>
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} sm={2}>
                <TextField
                  disabled
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Código *"
                  value={formData.tipcodigo}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.tipdescri}
                  onChange={(e) => handleInputChange("tipdescri", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Estado"
                  value={formData.tipstatus}
                  onChange={(e) => handleInputChange("tipstatus", e.target.value)}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="h6" color="primary" gutterBottom>
              Datos de Depreciación
            </Typography>
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} sm={3}>
                <TextField
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Años Vida Útil"
                  value={formData.tipaniodep}
                  onChange={(e) => handleInputChange("tipaniodep", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Meses Vida Útil"
                  value={formData.tipmesdep}
                  onChange={(e) => handleInputChange("tipmesdep", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="% Valor Residual"
                  value={formData.tipporresidual}
                  onChange={(e) => handleInputChange("tipporresidual", e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="h6" color="primary" gutterBottom>
              Integración Contable
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={listaCuentas}
                  getOptionLabel={(option) => option.label || ""}
                  // LÓGICA DE FOCO: Busca en la lista y si no lo encuentra, dibuja temporalmente el código guardado en BD
                  value={
                    listaCuentas.find((c) => c.id === formData.tipctacon) ||
                    (formData.tipctacon ? { id: formData.tipctacon, label: `${formData.tipctacon}` } : null)
                  }
                  onChange={(event, newValue) => handleInputChange("tipctacon", newValue ? newValue.id : "")}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Cuenta Activo Fijo" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={listaCuentas}
                  getOptionLabel={(option) => option.label || ""}
                  value={
                    listaCuentas.find((c) => c.id === formData.tipctadepre) ||
                    (formData.tipctadepre ? { id: formData.tipctadepre, label: `${formData.tipctadepre}` } : null)
                  }
                  onChange={(event, newValue) => handleInputChange("tipctadepre", newValue ? newValue.id : "")}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Cuenta Depreciación Acumulada" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={listaCuentas}
                  getOptionLabel={(option) => option.label || ""}
                  value={
                    listaCuentas.find((c) => c.id === formData.tipctagasdep) ||
                    (formData.tipctagasdep ? { id: formData.tipctagasdep, label: `${formData.tipctagasdep}` } : null)
                  }
                  onChange={(event, newValue) => handleInputChange("tipctagasdep", newValue ? newValue.id : "")}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Cuenta Gasto Depreciación" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EditarTiposPPE
